import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { useInView } from 'react-intersection-observer';
import CommentSheet from '../components/feed/CommentSheet';
import CreatePostModal from '../components/feed/CreatePostModal';
import PostCard from '../components/feed/PostCard';
import ReportModal from '../components/feed/ReportModal';
import SearchBar from '../components/community/SearchBar';
import TagChips from '../components/community/TagChips';
import { useAppStore } from '../stores/wpAppStore';
import type { WPPost } from '../services/wordpressApi';
import * as wpApi from '../services/wordpressApi';
import { isNativeApp } from '../lib/isNativeApp';
import { Sparkles, Plus } from 'lucide-react';

const TRENDING_TAGS = ['#Snooker', '#Pool9', '#3C', '#Libre', '#Tournament'];

const styles = {
  container: {
    minHeight: '100vh',
    background: '#09090b',
    padding: '70px 16px calc(80px + env(safe-area-inset-bottom, 0px))',
    width: '100%',
    maxWidth: '760px',
    margin: '0 auto',
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  titleSection: {},
  title: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#fafafa',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#71717a',
    marginTop: '2px',
  },
  createBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s',
  },
  searchSection: {
    marginBottom: '12px',
  },
  tagsSection: {
    marginBottom: '16px',
  },
  errorBanner: {
    padding: '12px 16px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px',
  },
  refreshingIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #27272a',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  refreshingText: {
    fontSize: '13px',
    color: '#a1a1aa',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#fafafa',
    margin: '0 0 8px 0',
  },
  emptyText: {
    fontSize: '14px',
    color: '#71717a',
    margin: 0,
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    marginTop: '16px',
    transition: 'all 0.2s',
  },
  postsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  loadMoreContainer: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingMore: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  loadingMoreText: {
    fontSize: '13px',
    color: '#a1a1aa',
  },
};

interface FeedPageProps {
  onUserClick?: (userId: number) => void;
}

export default function FeedPage({ onUserClick }: FeedPageProps) {
  const posts = useAppStore((s) => s.posts);
  const loading = useAppStore((s) => s.loading);
  const refreshing = useAppStore((s) => s.refreshing);
  const activeHashtag = useAppStore((s) => s.activeHashtag);
  const hasMore = useAppStore((s) => s.hasMore);
  const loadFeed = useAppStore((s) => s.loadFeed);
  const loadMore = useAppStore((s) => s.loadMore);
  const setActiveHashtag = useAppStore((s) => s.setActiveHashtag);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const deletePost = useAppStore((s) => s.deletePost);
  const refreshFeed = useAppStore((s) => s.refreshFeed);

  const isNative = isNativeApp();

  const [searchText, setSearchText] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [commentTarget, setCommentTarget] = useState<WPPost | null>(null);
  const [reportTarget, setReportTarget] = useState<WPPost | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef<number | null>(null);
  const pullDistanceRef = useRef(0);
  const modalOpenRef = useRef(false);

  useEffect(() => {
    modalOpenRef.current = Boolean(commentTarget || showCreatePost || reportTarget);
  }, [commentTarget, showCreatePost, reportTarget]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) === 0;

    const onTouchStart = (e: TouchEvent) => {
      if (modalOpenRef.current || useAppStore.getState().refreshing) return;
      if (!atTop()) return;
      touchStartY.current = e.touches[0].clientY;
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (modalOpenRef.current || useAppStore.getState().refreshing) return;
      if (touchStartY.current === null) return;
      const delta = e.touches[0].clientY - touchStartY.current;
      if (delta <= 0) return;
      if (!atTop()) return;
      e.preventDefault();
      const next = Math.min(delta * 0.5, 120);
      pullDistanceRef.current = next;
      setPullDistance(next);
    };

    const finish = () => {
      if (pullDistanceRef.current > 64 && !useAppStore.getState().refreshing) {
        useAppStore.getState().refreshFeed();
      }
      touchStartY.current = null;
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', finish);
    el.addEventListener('touchcancel', finish);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', finish);
      el.removeEventListener('touchcancel', finish);
    };
  }, []);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => setSearchQuery(value.trim()), 350);
    },
    [setSearchQuery],
  );

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (inView && hasMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loading, loadMore]);

  const handleSharePost = useCallback(async (post: WPPost) => {
    const text = [
      post.content,
      post.matchData
        ? `Kết quả: ${post.matchData.players.map((item) => `${item.name} ${item.score}`).join(' - ')}`
        : null,
    ]
      .filter(Boolean)
      .join('\n');

    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: post.ownerName, text });
      try {
        await wpApi.sharePost(post.id);
      } catch {
        // keep quiet
      }
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success('Đã sao chép nội dung để chia sẻ');
    }
  }, []);

  const handleDeletePost = useCallback(async (postId: number) => {
    try {
      await deletePost(postId);
      toast.success('Đã xóa bài viết');
    } catch {
      // Error message được App watcher chuyển thành toast
    }
  }, [deletePost]);

  return (
    <div
      ref={containerRef}
      className={`feed-page mx-auto min-h-screen w-full max-w-[760px] bg-zinc-950 px-2 pb-[calc(16px+env(safe-area-inset-bottom))] ${
        isNative ? 'pt-4' : 'pt-[70px]'
      } sm:px-4`}
      style={{ overscrollBehaviorY: 'none' }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
        html, body { overscroll-behavior-y: none; }
      `}</style>

      {/* Pull-to-refresh indicator */}
      {pullDistance > 0 && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden"
          style={{ height: pullDistance }}
        >
          <motion.div
            className="h-5 w-5 rounded-full border-2 border-zinc-800 border-t-blue-500"
            animate={refreshing ? { rotate: 360 } : { rotate: pullDistance > 64 ? 180 : 0 }}
            transition={refreshing
              ? { duration: 1, repeat: Infinity, ease: 'linear' }
              : { duration: 0.2 }}
          />
          <span className="text-[12px] text-zinc-400">
            {refreshing ? 'Đang cập nhật...' : pullDistance > 64 ? 'Thả ra để làm mới' : 'Kéo xuống để làm mới'}
          </span>
        </div>
      )}

      {/* Top bar: title + create button */}
      <motion.div 
        className="mb-4 flex items-center justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <h1 className="text-2xl font-extrabold text-zinc-50">Khám phá</h1>
          <p className="mt-0.5 text-xs text-zinc-500">Cộng đồng Bi-a Việt Nam</p>
        </div>
        <motion.button 
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-[14px] border-0 bg-gradient-to-br from-blue-500 to-blue-600 text-2xl text-white shadow-lg shadow-blue-500/30"
          onClick={() => setShowCreatePost(true)}
          whileHover={{ 
            scale: 1.1,
            boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
          }}
          whileTap={{ scale: 0.9 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Plus size={24} />
        </motion.button>
      </motion.div>

      {/* Search bar */}
      <motion.div
        className="mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      >
        <SearchBar
          value={searchText}
          placeholder="Tìm kiếm bài viết, người chơi..."
          onChange={handleSearchChange}
          onClear={() => {
            setSearchText('');
            setSearchQuery('');
          }}
        />
      </motion.div>

      {/* Tag chips */}
      <motion.div
        className="mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      >
        <TagChips
          tags={TRENDING_TAGS}
          activeTag={activeHashtag}
          onTagPress={(tag) => setActiveHashtag(activeHashtag === tag ? null : tag)}
          onClear={() => setActiveHashtag(null)}
        />
      </motion.div>

      {/* Refreshing indicator */}
      <AnimatePresence>
        {refreshing && (
          <motion.div
            className="mb-4 flex items-center justify-center gap-2 p-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="h-5 w-5 rounded-full border-2 border-zinc-800 border-t-blue-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            <span className="text-[13px] text-zinc-400">Đang cập nhật...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <AnimatePresence mode="popLayout">
        {posts.length === 0 && !loading ? (
          <motion.div 
            key="empty-state"
            className="flex flex-col items-center justify-center px-5 py-[60px] text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <span className="mb-4 text-6xl">🎱</span>
            <h2 className="mb-2 text-xl font-bold text-zinc-50">Chưa có bài viết nào</h2>
            <span className="text-sm text-zinc-500">Hãy là người đầu tiên chia sẻ!</span>
            <motion.button
              className="mt-4 flex items-center justify-center gap-2 rounded-xl border-0 bg-gradient-to-br from-blue-500 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30"
              onClick={() => setShowCreatePost(true)}
              whileHover={{ scale: 1.05, boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
            >
              <Sparkles size={16} />
              Tạo bài viết
            </motion.button>
          </motion.div>
        ) : (
          <motion.div key="posts-container" className="flex flex-col gap-4">
            <AnimatePresence mode="popLayout">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ 
                    duration: 0.4, 
                    delay: Math.min(index * 0.05, 0.3),
                    ease: [0.16, 1, 0.3, 1]
                  }}
                  layout
                >
                  <PostCard
                    post={post}
                    onComment={(item) => setCommentTarget(item)}
                    onShare={handleSharePost}
                    onDelete={handleDeletePost}
                    onReport={(item) => setReportTarget(item)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Load More sentinel */}
      <div ref={loadMoreRef} className="h-5">
        <AnimatePresence>
          {loading && posts.length > 0 && (
            <motion.div
              className="flex items-center justify-center gap-2 p-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="h-5 w-5 rounded-full border-2 border-zinc-800 border-t-blue-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <span className="text-[13px] text-zinc-400">Đang tải thêm...</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <CreatePostModal open={showCreatePost} onClose={() => setShowCreatePost(false)} />
      <CommentSheet
        open={commentTarget !== null}
        post={commentTarget}
        onClose={() => setCommentTarget(null)}
      />
      <ReportModal
        post={reportTarget}
        onClose={() => setReportTarget(null)}
      />
    </div>
  );
}
